import { KeyboardArrowUp, KeyboardArrowDown } from "@mui/icons-material"
import { Box, Button, Collapse, Typography } from "@mui/material";
import { useState, type ReactNode } from "react";
import { alpha } from "@mui/material/styles";

interface ConfigDropdownFormProps {
  title: string;
  subtitle?: string;
  children: ReactNode;
  onSave?: () => void;
  loading?: boolean;
  defaultOpen?: boolean;
}

export const ConfigDropdownForm = ({
  title,
  subtitle,
  children,
  onSave,
  loading = false,
  defaultOpen = false,
}: ConfigDropdownFormProps) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <Box
      sx={{
        mb: 3,
        borderRadius: 4,
        background: (theme) => alpha(theme.palette.background.paper, 0.75),
        backdropFilter: 'blur(10px)',
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: (theme) => `0 4px 20px ${alpha(theme.palette.primary.main, 0.06)}`,
        overflow: 'hidden',
        transition: 'box-shadow 0.2s ease',
        '&:hover': {
          boxShadow: (theme) => `0 6px 28px ${alpha(theme.palette.primary.main, 0.10)}`,
        },
      }}
    >
      {/* Header / toggle row */}
      <Box
        onClick={() => setIsOpen(!isOpen)}
        sx={{
          px: 3,
          py: 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          userSelect: 'none',
          borderBottom: isOpen ? '1px solid rgba(0,0,0,0.06)' : 'none',
          transition: 'background 0.15s ease',
          '&:hover': {
            background: 'rgba(0,0,0,0.02)',
          },
        }}
      >
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: 'text.primary' }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            height: 32,
            borderRadius: 2,
            bgcolor: (theme) => alpha(theme.palette.text.secondary, 0.08),
            color: 'text.secondary',
            transition: 'background 0.15s ease',
            flexShrink: 0,
          }}
        >
          {isOpen ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
        </Box>
      </Box>

      <Collapse in={isOpen}>
        <Box sx={{ px: 3, pt: 3, pb: 3 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {children}
          </Box>

          {onSave && (
            <Button
              variant="contained"
              onClick={onSave}
              disabled={loading}
              fullWidth
              sx={{ mt: 3, py: 1.5, fontWeight: 700, borderRadius: 2 }}
            >
              {loading ? "Salvando..." : "Salvar Alterações"}
            </Button>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};